// De bronpil — het Perplexity-idioom in onze huisstijl.
//
// Perplexity zet aan het eind van een claim een chip met de UITGEVERSNAAM plus
// een teller: `npuls +4`. Dat is het punt dat in de opname werd aangewezen: één
// verwijzing draagt meerdere bronnen. Een genummerd bolletje zegt niets ("vijf
// bronnen? geen idee"), een uitgeversnaam zegt meteen iets.
//
// Wat wij eraan toevoegen omdat wij het hebben en zij niet: de volgorde is op
// GEZAG (het zwaarste huis staat in de pil), en de bovenste bron toont zijn
// verbatim citaat. De rest is één regel per bron — vijf citaten in een popover
// zou de bijlage terugbrengen die hier nu net uit gaat.

import { useState } from "react";

import { color, radius, size, weight } from "../tokens";
import { heeftBewijsvelden, WIE_KORT, WIE_WOORD, wieTelling } from "./Bewijs";
import type { Bronbundel } from "../lib/kaartas";
import { bronTitel, datumNL } from "../lib/kaartas";

export function Bronpil({ bundels }: { bundels: Bronbundel[] }) {
  const [open, setOpen] = useState(false);
  if (bundels.length === 0) return null;

  const eerste = bundels[0]!;
  const rest = bundels.length - 1;
  const gezagen = bundels.map((b) => b.bron.gezag).filter((x): x is number => typeof x === "number");
  const gemiddeld = gezagen.length
    ? (gezagen.reduce((a, b) => a + b, 0) / gezagen.length).toFixed(1).replace(".", ",")
    : null;
  // Methode v3: dragen de bronnen het wie-veld, dan telt de kop per wie
  // ("2 instituten · 1 vakblad") en toont het vakje INST/VAK/BEDR.
  const v3 = bundels.some((b) => heeftBewijsvelden(b.bron));
  const telling = v3 ? wieTelling(bundels.map((b) => b.bron)) : null;

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10.5,
          fontWeight: weight.medium,
          fontFamily: "inherit",
          color: open ? color.surface : color.soft,
          background: open ? color.ink : color.surfaceQuote,
          border: `1px solid ${open ? color.ink : color.border}`,
          borderRadius: 5,
          padding: "1.5px 7px",
          marginLeft: 4,
          lineHeight: 1.55,
          cursor: "pointer",
          verticalAlign: "baseline",
        }}
      >
        {eerste.bron.uitgever}
        {rest > 0 && (
          <span style={{ color: open ? "#cbd5e1" : color.faint, fontWeight: weight.regular }}>
            +{rest}
          </span>
        )}
      </button>

      {open && (
        <>
          <span
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <span
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              width: 340,
              maxWidth: "min(340px, calc(100vw - 40px))",
              background: color.surface,
              border: `1px solid ${color.border}`,
              borderRadius: radius.block,
              boxShadow: "0 10px 30px rgba(17,24,39,.13)",
              padding: "11px 13px",
              zIndex: 50,
              display: "block",
              textAlign: "left",
              whiteSpace: "normal",
            }}
          >
            <span
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: size.columnLabel,
                  fontWeight: weight.medium,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: color.faint,
                }}
              >
                {/* "alinea": de pil staat per alinea gevuld uit de
                    kaartbronnen (feedkaart-ADR §3.1) — "zin" was gejokt,
                    zelfde correctie als in de chat (28-08). */}
                {bundels.length} {bundels.length === 1 ? "bron" : "bronnen"} onder deze alinea
              </span>
              {v3
                ? telling && (
                    <span style={{ fontSize: size.label, color: color.faint }}>{telling}</span>
                  )
                : gemiddeld && (
                    <span style={{ fontSize: size.label, color: color.faint }}>gezag {gemiddeld}</span>
                  )}
            </span>

            {bundels.slice(0, 3).map((b, i) => (
              <span
                key={`${b.bron.documentId}-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "26px 1fr",
                  gap: 9,
                  padding: "7px 0",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                }}
              >
                <span
                  title={
                    v3 && b.bron.wie
                      ? [WIE_WOORD[b.bron.wie], b.bron.onderbouwingLabel].filter(Boolean).join(" · ")
                      : undefined
                  }
                  style={{
                    width: 26,
                    height: 20,
                    borderRadius: 5,
                    background: color.surfaceQuote,
                    border: `1px solid ${color.border}`,
                    fontSize: 9.5,
                    fontWeight: weight.bold,
                    color: color.soft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {v3
                    ? (b.bron.wie ? WIE_KORT[b.bron.wie] : "—")
                    : b.bron.gezag != null
                      ? String(b.bron.gezag).replace(".0", "")
                      : "—"}
                </span>
                <span style={{ display: "block", minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: weight.medium,
                      color: color.ink,
                    }}
                  >
                    {b.bron.uitgever}
                  </span>
                  <span style={{ display: "block", fontSize: size.label, color: color.faint }}>
                    {[bronTitel(b.bron).slice(0, 52), datumNL(b.bron.datum)].filter(Boolean).join(" · ")}
                  </span>
                  {i === 0 && b.bron.quote && (
                    <span
                      style={{
                        display: "block",
                        marginTop: 5,
                        borderLeft: `2px solid ${color.border}`,
                        paddingLeft: 9,
                        fontSize: 11.5,
                        color: color.muted,
                        fontStyle: "italic",
                        lineHeight: 1.55,
                      }}
                    >
                      “{b.bron.quote.replace(/\s+/g, " ").slice(0, 180)}
                      {b.bron.quote.length > 180 ? "…" : ""}”
                    </span>
                  )}
                </span>
              </span>
            ))}

            <span
              style={{
                display: "block",
                paddingTop: 8,
                marginTop: 4,
                borderTop: `1px solid ${color.lineFaint}`,
                fontSize: size.label,
                color: color.faint,
              }}
            >
              {bundels.length > 3 && `+ ${bundels.length - 3} meer · `}
              <span style={{ color: color.accentBlue, fontWeight: weight.medium }}>
                alle {bundels.length} openen →
              </span>
            </span>
          </span>
        </>
      )}
    </span>
  );
}

/**
 * De rij onderaan de open kaart (variant O2): dezelfde pillen, zonder tekst
 * eromheen. Boven de zes klapt de rest uit in plaats van weggelaten te worden —
 * dat is de O3-overloop uit het besluit.
 */
export function Bronrij({ bundels, max = 6 }: { bundels: Bronbundel[]; max?: number }) {
  const [uit, setUit] = useState(false);
  const zichtbaar = uit ? bundels : bundels.slice(0, max);
  const rest = bundels.length - zichtbaar.length;
  const passages = bundels.reduce((n, b) => n + b.passages, 0);
  return (
    <div>
      {/* "Gelezen 2 · geciteerd 4" was niet te begrijpen (owner 26-08): het
          leek te zeggen dat het platform maar twee documenten gelezen had. Het
          gaat om DEZE kaart, en de twee getallen zijn de passages en de
          rapporten waaruit ze komen. Nu staat dat er gewoon. */}
      <div style={{ fontSize: size.label, color: color.faint, marginBottom: 7 }}>
        Onderbouwd met{" "}
        <b style={{ color: color.soft, fontWeight: weight.medium }}>
          {passages} {passages === 1 ? "passage" : "passages"}
        </b>{" "}
        uit{" "}
        <b style={{ color: color.soft, fontWeight: weight.medium }}>
          {bundels.length} {bundels.length === 1 ? "bron" : "bronnen"}
        </b>
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
        {zichtbaar.map((b, i) => (
          <Bronpil key={`${b.bron.documentId ?? b.bron.uitgever}-${i}`} bundels={[b]} />
        ))}
        {(rest > 0 || uit) && (
          <button
            onClick={() => setUit(!uit)}
            style={{
              fontSize: 10.5,
              color: color.soft,
              background: "none",
              border: `1px dashed ${color.border}`,
              borderRadius: 5,
              padding: "1.5px 7px",
              marginLeft: 4,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {uit ? "minder tonen" : `+${rest} ▾`}
          </button>
        )}
      </div>
    </div>
  );
}
